import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteGuestRevisionContentApi,
  deleteRevisionContentApi,
  getGuestRevisionContentsApi,
  getRevisionContentsApi,
} from "../services/api";
import { showSnackBar } from "../utils/commonServices";
import { findRound } from "../utils/revisionContent";

/**
 * The files attached to one revision request: `GET /api/v2/revision_contents`
 * plus `DELETE /api/v2/revision_contents/:id`.
 *
 * Kept in local state rather than redux. `url` and `download_url` are presigned
 * and expire, and `deletable` is the server's answer *for this viewer*, so none
 * of it may be cached across mounts or shared between the user and guest pages.
 *
 * Both callers narrow the request with `revision_id`: a guest is refused
 * without it, and a user would otherwise receive every round of the project when
 * only the open request is on screen.
 *
 * @param {object} options
 * @param {number} options.eventId    project id (required by the endpoint)
 * @param {number} options.revisionId the revision whose files to show
 * @param {boolean} options.isGuest   guest link rather than a signed-in user
 * @param {number} options.guestUserId guests only
 * @param {string} options.collabCode guests only - refused (403) without it
 * @param {boolean} options.enabled   fetch on mount and when the ids change
 * @param {*} options.refreshKey      refetch whenever this changes; submitting a
 *                                    revision rewrites `round` and `deletable`
 *                                    on every row without changing any id
 */
export default ({ eventId, revisionId, isGuest = false, guestUserId, collabCode, enabled = true, refreshKey }) => {
  const dispatch = useDispatch();
  const uploadReducer = useSelector((state) => state.root.uploadReducer);
  const [rounds, setRounds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  // A slow response for a revision the user has already navigated away from
  // must not overwrite the list of the one now on screen.
  const requestKeyRef = useRef(null);

  const canFetch = !!eventId && !!revisionId && (!isGuest || (!!guestUserId && !!collabCode));

  const fetchContents = useCallback(() => {
    if (!canFetch) {
      setRounds([]);
      setIsLoading(false);
      return Promise.resolve();
    }

    const requestKey = `${eventId}:${revisionId}:${guestUserId || ''}`;
    requestKeyRef.current = requestKey;
    setIsLoading(true);
    setError(null);

    const params = { event_id: eventId, revision_id: revisionId };
    const request = isGuest
      ? getGuestRevisionContentsApi({ ...params, guest_user_id: guestUserId, collab_code: collabCode })
      : getRevisionContentsApi(params);

    return request
      .then((response) => {
        if (requestKeyRef.current !== requestKey) return;
        const responseObject = response?.data;
        if (responseObject?.http_code === 200) {
          setRounds(Array.isArray(responseObject?.data?.rounds) ? responseObject.data.rounds : []);
        } else {
          setRounds([]);
          setError({ http_code: responseObject?.http_code, message: responseObject?.message });
        }
        setIsLoading(false);
      })
      .catch((error) => {
        if (requestKeyRef.current !== requestKey) return;
        const errorObject = error?.response?.data;
        const httpCode = errorObject?.http_code || error?.response?.status;
        setRounds([]);
        setError({ http_code: httpCode, message: errorObject?.message, error_code: errorObject?.error_code });
        // 401 is the only status the shared handler should see - it logs the
        // user out. Everything else is rendered inside the panel, where the
        // user can see which list failed.
        if (httpCode === 401) showSnackBar(dispatch, errorObject || { http_code: 401 });
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch, eventId, revisionId, isGuest, guestUserId, collabCode, dispatch, refreshKey]);

  useEffect(() => {
    if (enabled) fetchContents();
  }, [enabled, fetchContents]);

  useEffect(() => {
    // A file finished uploading somewhere on the page. A file appears in the
    // list as "uploading" from pre_flight onwards, so this is what turns it
    // into a completed row with a working link.
    if (enabled && uploadReducer?.revision_content_refresh) fetchContents();
  }, [enabled, uploadReducer?.revision_content_refresh, fetchContents]);

  const deleteFile = useCallback((fileId, onDeleted) => {
    if (!fileId) return Promise.resolve();
    setDeletingId(fileId);

    const request = isGuest
      ? deleteGuestRevisionContentApi({ id: fileId, param: { guest_user_id: guestUserId, collab_code: collabCode } })
      : deleteRevisionContentApi({ id: fileId, param: {} });

    return request
      .then((response) => {
        setDeletingId(null);
        if (response?.data?.http_code === 200) {
          // Drop the row immediately, then resync - the server is the authority
          // on what is left and on who may still delete it.
          setRounds((previous) => previous.map((round) => ({
            ...round,
            files: Array.isArray(round?.files) ? round.files.filter((file) => file?.id !== fileId) : round?.files,
          })));
          onDeleted && onDeleted();
        }
        fetchContents();
      })
      .catch((error) => {
        setDeletingId(null);
        const errorObject = error?.response?.data;
        const httpCode = errorObject?.http_code || error?.response?.status;
        showSnackBar(dispatch, errorObject || { http_code: httpCode });
        // 404 means it is already gone and 409 means the revision closed under
        // the user; both are answered by showing what is actually there now.
        if (httpCode === 404 || httpCode === 409) fetchContents();
      });
  }, [isGuest, guestUserId, collabCode, dispatch, fetchContents]);

  const round = findRound(rounds, revisionId);

  return {
    rounds,
    round,
    files: Array.isArray(round?.files) ? round.files : [],
    counts: round?.content_counts || null,
    submitted: round?.submitted === true,
    isLoading,
    error,
    deletingId,
    deleteFile,
    refetch: fetchContents,
  };
};
